import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Flame, Zap, Target, TrendingUp, Sparkles, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { skillPaths, skillPathBySlug } from '../data/learningContent';
import { useDocumentMeta } from '../lib/useDocumentMeta';
import { getSkillLabel, SKILL_AREAS, computeOverallScore } from '../data/skillAreas';
import SkillScorePanel from '../components/SkillScorePanel';

interface Challenge {
  id: string;
  slug: string;
  title: string;
  description: string;
  duration_minutes: number;
  skill_areas: string[];
  difficulty: string;
  prompt_text: string;
  estimated_xp: number;
  sort_order: number;
}

interface ProgressRow {
  lesson_slug: string;
  status: string;
}

interface ScoreHistoryRow {
  overall_score: number;
  recorded_at: string;
}

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

export default function LearnPage() {
  const { user, profile, refreshProfile } = useAuth();
  const [progress, setProgress] = useState<ProgressRow[]>([]);
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [completedToday, setCompletedToday] = useState<Set<string>>(new Set());
  const [skillScores, setSkillScores] = useState<Record<string, number>>({});
  const [scoreHistory, setScoreHistory] = useState<ScoreHistoryRow[]>([]);
  const [todayScore, setTodayScore] = useState<number | null>(null);
  const [loaded, setLoaded] = useState(false);

  useDocumentMeta({
    title: 'Today — PromptGPT',
    description: 'Your daily AI workout. One challenge, your skill score, your streak.',
    canonical: 'https://promptgpt.ai/learn',
  });

  useEffect(() => {
    if (!user) return;
    supabase.rpc('record_activity', { p_kind: 'learn_visit' }).then(() => refreshProfile());
  }, [user, refreshProfile]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const [{ data: prog }, scoreRes] = await Promise.all([
        supabase.from('user_progress').select('lesson_slug, status').eq('user_id', user.id),
        supabase.from('skill_scores').select('skill_area, score').eq('user_id', user.id),
      ]);
      setProgress((prog as ProgressRow[]) || []);

      const scores: Record<string, number> = {};
      (scoreRes.data || []).forEach((r: any) => {
        scores[r.skill_area || r.dimension] = r.score;
      });
      setSkillScores(scores);

      const { data: history } = await supabase
        .from('skill_score_history')
        .select('overall_score, recorded_at')
        .eq('user_id', user.id)
        .order('recorded_at', { ascending: false })
        .limit(14);
      setScoreHistory((history as ScoreHistoryRow[]) || []);

      try {
        const challengeUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/grade-challenge`;
        const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch(challengeUrl, {
          headers: {
            'Content-Type': 'application/json',
            'apikey': anonKey || '',
            'Authorization': `Bearer ${session?.access_token || anonKey || ''}`,
          },
        });
        if (res.ok) {
          const data = await res.json();
          setChallenges(data.challenges || []);
          setCompletedToday(new Set(data.completedToday || []));
          if (data.skillScores) setSkillScores(data.skillScores);
        }
      } catch {}

      setLoaded(true);
    })();
  }, [user]);

  const userPath = useMemo(
    () => (profile?.role && skillPathBySlug(profile.role)) || skillPaths[0],
    [profile?.role],
  );

  const completedSet = useMemo(
    () => new Set(progress.filter((p) => p.status === 'completed').map((p) => p.lesson_slug)),
    [progress],
  );

  const overallScore = useMemo(() => computeOverallScore(skillScores), [skillScores]);

  const weeklyDelta = useMemo(() => {
    if (scoreHistory.length < 2) return 0;
    const now = scoreHistory[0]?.overall_score ?? overallScore;
    const weekAgo = scoreHistory.find((h) => {
      const days = (new Date(scoreHistory[0].recorded_at).getTime() - new Date(h.recorded_at).getTime()) / 86400000;
      return days >= 6;
    });
    const oldScore = weekAgo?.overall_score ?? scoreHistory[scoreHistory.length - 1]?.overall_score ?? 0;
    return Math.max(0, now - oldScore);
  }, [scoreHistory, overallScore]);

  const allDoneToday = challenges.length > 0 && challenges.every((c) => completedToday.has(c.id));
  const isEmpty = Object.values(skillScores).every((v) => !v || v === 0);

  if (!loaded) {
    return (
      <div className="container-app py-16 max-w-3xl">
        <div className="space-y-4">
          <div className="h-8 w-48 bg-ink-100 rounded-lg animate-pulse" />
          <div className="h-32 bg-ink-100 rounded-2xl animate-pulse" />
          <div className="h-48 bg-ink-100 rounded-2xl animate-pulse" />
        </div>
      </div>
    );
  }

  const firstName = (profile?.full_name || user?.email || '').split(' ')[0] || 'there';
  const allLessons = userPath?.modules.flatMap((m, mi) =>
    m.lessons.map((l) => ({ ...l, moduleIdx: mi, moduleTitle: m.title })),
  ) || [];
  const nextLesson = allLessons.find((l) => !completedSet.has(l.slug));
  const doneCount = allLessons.filter((l) => completedSet.has(l.slug)).length;

  const recommendedChallenge = challenges.find((c) => !completedToday.has(c.id));

  const weakestSkill = SKILL_AREAS.reduce((min, s) =>
    (skillScores[s.key] ?? 0) < (skillScores[min.key] ?? 0) ? s : min,
  SKILL_AREAS[0]);

  return (
    <div className="container-app py-10 sm:py-12 max-w-3xl">
      {/* Greeting */}
      <div className="mb-2 text-sm text-ink-500">
        {getGreeting()}, {firstName}
      </div>

      {/* Stats strip */}
      <div className="flex items-center gap-4 mb-8 flex-wrap">
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-sun-50 border border-sun-100">
          <Flame className="w-5 h-5 text-sun-500" />
          <div>
            <div className="text-[10px] uppercase tracking-wider text-sun-600 font-semibold">Streak</div>
            <div className="text-lg font-bold text-sun-700 leading-none">{profile?.current_streak ?? 0}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-accent-50 border border-accent-100">
          <Zap className="w-5 h-5 text-accent-600" />
          <div>
            <div className="text-[10px] uppercase tracking-wider text-accent-600 font-semibold">Skill Score</div>
            <div className="text-lg font-bold text-accent-700 leading-none">{overallScore}</div>
          </div>
        </div>
        {weeklyDelta > 0 && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-mint-50 border border-mint-100">
            <TrendingUp className="w-5 h-5 text-mint-600" />
            <div>
              <div className="text-[10px] uppercase tracking-wider text-mint-600 font-semibold">This week</div>
              <div className="text-lg font-bold text-mint-700 leading-none">+{weeklyDelta}</div>
            </div>
          </div>
        )}
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-warm-100 border border-ink-100">
          <Sparkles className="w-5 h-5 text-ink-500" />
          <div>
            <div className="text-[10px] uppercase tracking-wider text-ink-500 font-semibold">Level</div>
            <div className="text-lg font-bold text-ink-700 leading-none">{profile?.level ?? 1}</div>
          </div>
        </div>
      </div>

      {/* Headline */}
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-500 mb-3">
          {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl mb-3 leading-tight">
          Your 7-minute AI workout.
        </h1>
        <p className="text-ink-600 text-lg leading-relaxed">
          One challenge. Real practice. Get graded and level up your skills.
        </p>
      </div>

      {/* Today's challenge or completed-today celebration */}
      {allDoneToday && challenges.length > 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="mb-8"
        >
          <div className="bg-mint-50 border border-mint-200 rounded-2xl p-8 text-center">
            <CheckCircle2 className="w-10 h-10 text-mint-600 mx-auto mb-3" />
            <h2 className="font-display text-2xl mb-2">Done for today</h2>
            <p className="text-ink-600 text-sm mb-6">
              You completed all {challenges.length} challenges today. Come back tomorrow for a new set.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/practice" className="btn-primary">
                Bring Your Own Problem <Target className="w-4 h-4" />
              </Link>
              <Link to="/drills" className="btn-secondary">
                Try the daily drill
              </Link>
            </div>
          </div>
        </motion.div>
      ) : recommendedChallenge ? (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="mb-8"
        >
          <Link
            to="/challenges"
            className="block bg-white border border-ink-100 hover:border-ink-200 hover:shadow-soft rounded-2xl p-6 transition group"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-500">Today's challenge</span>
                  <span className="inline-flex items-center gap-1 text-xs text-ink-500">
                    <Clock className="w-3.5 h-3.5" /> {recommendedChallenge.duration_minutes} min
                  </span>
                </div>
                <h2 className="font-display text-2xl sm:text-3xl mb-2 leading-tight">{recommendedChallenge.title}</h2>
                <p className="text-ink-600 leading-relaxed">{recommendedChallenge.description}</p>
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {recommendedChallenge.skill_areas.map((area) => (
                    <span key={area} className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-accent-50 text-accent-700 border border-accent-100">
                      {getSkillLabel(area)}
                    </span>
                  ))}
                </div>
              </div>
              <div className="shrink-0">
                <div className="w-12 h-12 rounded-full bg-ink-900 grid place-items-center group-hover:scale-105 transition">
                  <ArrowRight className="w-5 h-5 text-warm-50" />
                </div>
              </div>
            </div>
          </Link>
        </motion.div>
      ) : null}

      {/* Bring Your Own Problem */}
      <Link
        to="/practice"
        className="flex items-center gap-4 bg-ink-900 hover:bg-ink-800 text-warm-50 rounded-2xl p-5 transition group mb-8"
      >
        <div className="w-10 h-10 rounded-full bg-accent-500 grid place-items-center shrink-0">
          <Target className="w-5 h-5 text-ink-900" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-warm-400 mb-0.5">Bring Your Own Problem</div>
          <div className="text-sm font-medium">What are you trying to get done?</div>
        </div>
        <ArrowRight className="w-4 h-4 text-warm-400 group-hover:text-warm-50 shrink-0 transition" />
      </Link>

      {/* Skill Score Panel */}
      <div className="bg-white border border-ink-100 rounded-2xl p-6 mb-8">
        {isEmpty ? (
          <div className="text-center py-6">
            <div className="w-12 h-12 rounded-full bg-accent-50 grid place-items-center mx-auto mb-3">
              <Zap className="w-6 h-6 text-accent-400" />
            </div>
            <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-500 mb-2">AI Skill Score</div>
            <p className="text-ink-600 text-sm leading-relaxed max-w-sm mx-auto">
              Complete your first challenge to start building your score across 10 AI skill areas.
            </p>
          </div>
        ) : (
          <SkillScorePanel scores={skillScores} overallScore={overallScore} weeklyDelta={weeklyDelta} />
        )}
      </div>

      {/* Continue learning */}
      {nextLesson && userPath && (
        <div className="mb-8">
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-500 mb-3">Continue learning</div>
          <Link
            to={`/skill-paths/${userPath.slug}/${nextLesson.slug}`}
            className="flex items-center gap-4 bg-white border border-ink-100 hover:border-ink-200 hover:shadow-soft rounded-2xl p-4 transition group"
          >
            <div className="w-10 h-10 rounded-full bg-warm-100 grid place-items-center shrink-0">
              <Clock className="w-5 h-5 text-ink-500" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-ink-900">{nextLesson.title}</div>
              <div className="text-xs text-ink-500">{userPath.title} · {doneCount} of {allLessons.length} completed</div>
            </div>
            <ArrowRight className="w-4 h-4 text-ink-400 group-hover:text-ink-700 shrink-0 transition" />
          </Link>
        </div>
      )}

      {/* Recommended next challenge based on weakest skill */}
      {!isEmpty && (
        <div className="mb-8">
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-500 mb-3">
            Focus area: {getSkillLabel(weakestSkill.key)}
          </div>
          <p className="text-sm text-ink-500 mb-4">
            Your lowest-scoring skill area. Find challenges that train this skill to level up faster.
          </p>
          <Link to="/challenges" className="btn-secondary">
            Browse all challenges <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}

      {/* Daily drill link */}
      <Link
        to="/drills"
        className="flex items-center gap-4 bg-white border border-ink-100 hover:border-sun-300 hover:shadow-soft rounded-2xl p-4 transition group"
      >
        <div className="w-10 h-10 rounded-full bg-sun-50 grid place-items-center shrink-0">
          <Target className="w-5 h-5 text-sun-500" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-ink-500 mb-0.5">Daily drill</div>
          <div className="text-sm font-medium text-ink-900">Today's practice challenge</div>
        </div>
        <ArrowRight className="w-4 h-4 text-ink-400 group-hover:text-ink-700 shrink-0 transition" />
      </Link>
    </div>
  );
}
